import type {
  CreateOfferRequest,
  OfferDetailResponse,
  UpdateOfferRequest,
} from "@codway-dev/shared";
import { stripShopifyGid } from "@codway-dev/shared";
import {
  buildTriggers as buildTriggersShared,
  readTriggers,
  type TriggerProduct,
} from "./triggers.mapping";

// ─── Types ────────────────────────────────────────────────────────────────────

export type BundlePricingAction =
  | "none"
  | "percentage_off"
  | "fixed_amount_off";

export type BundleType = "fixed" | "configurable";

export interface BundleEditorItem {
  _tempId: string;
  productId: string;
  variantId: string;
  productTitle: string;
  variantTitle?: string;
  imageUrl?: string;
  // Per-unit catalog price for this variant; used to derive the bundle's
  // base_price on save and to render previews.
  basePrice: number;
  quantity: number;
}

export interface BundleEditorOption {
  _tempId: string;
  productId: string;
  variantId: string;
  productTitle: string;
  variantTitle?: string;
  imageUrl?: string;
  basePrice: number;
}

export interface BundleEditorGroup {
  _tempId: string;
  // Stable id sent to the BE (used to key shopper selections at runtime).
  // Distinct from `_tempId` (which is regenerated on every load for UI keys).
  groupId: string;
  name: string;
  pickCount: number;
  options: BundleEditorOption[];
}

export interface BundleEditorState {
  internalName: string;
  isActive: boolean;

  // Stable bundle identifier — generated on first save, preserved on edit.
  // Required by OfferTargetSchema when type === "bundle".
  bundleId: string;

  // Trigger products — the product pages where this bundle will surface.
  // Empty array = no trigger configured (save is blocked).
  triggerProducts: TriggerProduct[];

  bundleType: BundleType;

  // Fixed bundle items (used when bundleType === "fixed")
  items: BundleEditorItem[];

  // Configurable bundle groups (used when bundleType === "configurable")
  groups: BundleEditorGroup[];

  // Bundle-level pricing
  basePrice: number;
  // 0 = "no MSRP set" (omitted from the payload). To hide the
  // crossed-out price even when set, use `hideCompareAtPrice`.
  compareAtPrice: number;
  // Explicit "hide the crossed-out price" flag. Stored separately from
  // compareAtPrice because 0 is a valid price (free) and shouldn't
  // double as a sentinel. Mirrors quantity-tiers.
  hideCompareAtPrice: boolean;

  // Offer-level pricing_logic (where the discount lives for bundles)
  pricingAction: BundlePricingAction;
  pricingAmount: number;
  pricingPercentage: number;

  // Display
  titleOverride: string;
  description: string;
  imageUrl: string;

  // Styles
  backgroundColor: string;
  borderColor: string;
  activeBorderColor: string;
  borderRadius: number;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export function getDefaultBundleEditorItem(): BundleEditorItem {
  return {
    _tempId: crypto.randomUUID(),
    productId: "",
    variantId: "",
    productTitle: "",
    basePrice: 0,
    quantity: 1,
  };
}

export function getDefaultBundleEditorOption(): BundleEditorOption {
  return {
    _tempId: crypto.randomUUID(),
    productId: "",
    variantId: "",
    productTitle: "",
    basePrice: 0,
  };
}

export function getDefaultBundleEditorGroup(): BundleEditorGroup {
  return {
    _tempId: crypto.randomUUID(),
    groupId: crypto.randomUUID(),
    name: "Nuevo grupo",
    pickCount: 1,
    options: [],
  };
}

export function getDefaultBundleEditorState(): BundleEditorState {
  return {
    internalName: "Nueva oferta",
    isActive: true,

    bundleId: crypto.randomUUID(),

    triggerProducts: [],

    bundleType: "fixed",

    items: [],
    groups: [],

    basePrice: 0,
    compareAtPrice: 0,
    hideCompareAtPrice: false,

    pricingAction: "none",
    pricingAmount: 0,
    pricingPercentage: 0,

    titleOverride: "",
    description: "",
    imageUrl: "",

    backgroundColor: "rgba(255,255,255,1)",
    borderColor: "rgba(224,224,224,1)",
    activeBorderColor: "rgba(0,116,191,1)",
    borderRadius: 8,
  };
}

// ─── Builders ─────────────────────────────────────────────────────────────────

export function buildPricingLogic(state: BundleEditorState) {
  type PricingLogic =
    | { action: "none" }
    | { action: "percentage_off"; value: number }
    | { action: "fixed_amount_off"; amount: number };

  if (state.pricingAction === "percentage_off") {
    return {
      action: "percentage_off",
      value: state.pricingPercentage,
    } satisfies PricingLogic;
  }
  if (state.pricingAction === "fixed_amount_off") {
    return {
      action: "fixed_amount_off",
      amount: state.pricingAmount,
    } satisfies PricingLogic;
  }
  return { action: "none" } satisfies PricingLogic;
}

function buildItemsDefinition(state: BundleEditorState) {
  if (state.bundleType === "fixed") {
    return {
      bundle_type: "fixed" as const,
      items: state.items.map((item) => ({
        variant_id: item.variantId,
        // Required by the order endpoint's Shopify-line refine
        // (variant + product). Omitted only for legacy items that
        // were loaded before this field was wired through — those
        // will fail at order submit time until the merchant re-picks.
        ...(item.productId ? { product_id: item.productId } : {}),
        quantity: item.quantity,
        display_meta: {
          title: composeItemTitle(item.productTitle, item.variantTitle),
          ...(item.imageUrl ? { image_url: item.imageUrl } : {}),
          // Per-item catalog price — stored as string per schema.
          price: item.basePrice.toString(),
        },
      })),
    };
  }
  return {
    bundle_type: "configurable" as const,
    groups: state.groups.map((g) => ({
      group_id: g.groupId,
      name: g.name,
      pick_count: g.pickCount,
      options: g.options.map((opt) => ({
        variant_id: opt.variantId,
        ...(opt.productId ? { product_id: opt.productId } : {}),
        display_meta: {
          title: composeItemTitle(opt.productTitle, opt.variantTitle),
          ...(opt.imageUrl ? { image_url: opt.imageUrl } : {}),
        },
      })),
    })),
  };
}

function composeItemTitle(
  productTitle: string,
  variantTitle: string | undefined
): string {
  if (!variantTitle || variantTitle === "Default Title") return productTitle;
  return `${productTitle} — ${variantTitle}`;
}

// Derives the bundle's catalog total for the fixed mode (sum of item
// prices × quantities). For configurable mode, the merchant configures
// `base_price` directly since the shopper's picks determine what's
// actually in the bundle and the schema demands a single fixed price.
function deriveFixedBasePrice(items: BundleEditorItem[]): number {
  return items.reduce((sum, item) => sum + item.basePrice * item.quantity, 0);
}

function buildDisplayData(state: BundleEditorState) {
  const effectiveBasePrice =
    state.bundleType === "fixed"
      ? deriveFixedBasePrice(state.items)
      : state.basePrice;

  return {
    component_type: "bundle" as const,
    base_price: effectiveBasePrice,
    ...(state.compareAtPrice > 0
      ? { compare_at_price: state.compareAtPrice }
      : {}),
    hide_compare_at_price: state.hideCompareAtPrice,
    component_placement: "embed" as const,
    items_definition: buildItemsDefinition(state),
    ...(state.titleOverride ? { title_override: state.titleOverride } : {}),
    ...(state.description ? { description: state.description } : {}),
    ...(state.imageUrl ? { image_url: state.imageUrl } : {}),
    styles: {
      background_color: state.backgroundColor || undefined,
      border_style: "solid" as const,
      border_color: state.borderColor || undefined,
      // Loose property — passed through by the schema, consumed by the
      // UI's `--bun-active-border-color` CSS var for the selected state.
      active_border_color: state.activeBorderColor || undefined,
      border_radius: state.borderRadius,
    },
  };
}

function buildTarget(state: BundleEditorState) {
  return {
    type: "bundle" as const,
    bundle_id: state.bundleId,
    display_meta: {
      ...(state.titleOverride ? { title: state.titleOverride } : {}),
      ...(state.imageUrl ? { image_url: state.imageUrl } : {}),
      ...(state.description ? { description: state.description } : {}),
    },
  };
}

// Triggers are now an explicit merchant input (Section 2 of the editor)
// rather than derived from item productIds — eliminates the
// load-then-save trigger-widening bug from the earlier auto-derived
// approach. `product_meta` round-trips title + image so the picker
// rows render correctly on reload.
function buildTriggers(state: BundleEditorState) {
  if (state.triggerProducts.length === 0) {
    return { rule_type: "all" as const };
  }
  return buildTriggersShared("specific_products", state.triggerProducts);
}

// ─── Public API ──────────────────────────────────────────────────────────────

// Collects every reason the bundle can't be saved, in display order. The
// editor uses this to gate the save bar and surface a checklist; the request
// builders use the first entry as the thrown error. Single source of truth so
// the client-side gate and the save-time guard can never drift apart.
export function getBundleValidationErrors(state: BundleEditorState): string[] {
  const errors: string[] = [];

  if (!state.internalName.trim()) {
    errors.push("Asígnale un nombre a la oferta.");
  }
  if (state.triggerProducts.length === 0) {
    errors.push("Selecciona al menos un producto trigger antes de guardar.");
  }
  if (state.bundleType === "fixed" && state.items.length === 0) {
    errors.push("Agrega al menos un item al bundle antes de guardar.");
  }
  if (state.bundleType === "configurable") {
    if (state.groups.length === 0) {
      errors.push(
        "Agrega al menos un grupo al bundle configurable antes de guardar."
      );
    }
    const emptyGroup = state.groups.find((g) => g.options.length === 0);
    if (emptyGroup) {
      errors.push(
        `El grupo "${emptyGroup.name}" no tiene opciones. Agrega opciones o elimina el grupo.`
      );
    }
    // Pick count can't exceed the number of options in the group —
    // otherwise the shopper could never satisfy it and the bundle would
    // be impossible to add to cart. Caught here at save time rather than
    // letting the merchant ship a broken offer.
    const overPickedGroup = state.groups.find(
      (g) => g.pickCount > g.options.length
    );
    if (overPickedGroup) {
      errors.push(
        `El grupo "${overPickedGroup.name}" pide ${overPickedGroup.pickCount} opciones pero solo tiene ${overPickedGroup.options.length}. Reduce el "Elegir (cantidad)" o agrega más opciones.`
      );
    }
  }

  return errors;
}

export function stateToCreateRequest(
  state: BundleEditorState
): CreateOfferRequest {
  const errors = getBundleValidationErrors(state);
  if (errors.length > 0) {
    throw new Error(errors[0]);
  }

  return {
    internal_name: state.internalName,
    is_active: state.isActive,
    priority: 0,
    config: {
      display_type: "bundle",
      display_data: buildDisplayData(state),
      pricing_logic: buildPricingLogic(state),
    },
    target: buildTarget(state),
    triggers: buildTriggers(state),
  };
}

export function stateToUpdateRequest(
  state: BundleEditorState,
  originalConfig?: OfferDetailResponse["config"] | null
): UpdateOfferRequest {
  const create = stateToCreateRequest(state);

  if (
    originalConfig?.display_type === "bundle" &&
    create.config?.display_type === "bundle"
  ) {
    return {
      ...create,
      config: {
        ...(originalConfig as Record<string, unknown>),
        ...create.config,
        display_data: {
          ...(originalConfig.display_data as Record<string, unknown>),
          ...create.config.display_data,
        } as typeof create.config.display_data,
      },
    };
  }

  return create;
}

// ─── Inverse mapping (BE → state) ────────────────────────────────────────────

function decomposeItemTitle(combined: string | undefined): {
  productTitle: string;
  variantTitle?: string;
} {
  if (!combined) return { productTitle: "" };
  // Split on em-dash with surrounding spaces (the separator we emit in
  // `composeItemTitle`). Anything else is treated as a single product
  // title — keeps tolerance for hand-edited or legacy data.
  const idx = combined.indexOf(" — ");
  if (idx === -1) return { productTitle: combined };
  return {
    productTitle: combined.slice(0, idx),
    variantTitle: combined.slice(idx + 3),
  };
}

export function offerToState(offer: OfferDetailResponse): BundleEditorState {
  const config = offer.config;

  if (config.display_type !== "bundle") {
    throw new Error(
      `Este editor solo admite ofertas de tipo "bundle". La oferta "${offer.internal_name}" es de tipo "${config.display_type}".`
    );
  }

  const dd = config.display_data;
  const styles = (dd.styles ?? {}) as Record<string, unknown>;
  const defaults = getDefaultBundleEditorState();

  const target = offer.target;

  // Inverse-map pricing_logic (offer-level for bundles). Falls back to
  // "none" for actions the bundle editor doesn't expose (e.g. legacy
  // offers persisted with `fixed_price_per_item` — see note on
  // `BundlePricingAction` above).
  const p = config.pricing_logic;
  let pricingAction: BundlePricingAction = "none";
  let pricingAmount = 0;
  let pricingPercentage = 0;
  if (p.action === "percentage_off") {
    pricingAction = "percentage_off";
    pricingPercentage = p.value;
  } else if (p.action === "fixed_amount_off") {
    pricingAction = "fixed_amount_off";
    pricingAmount = p.amount;
  }

  // Switch on the discriminator directly so TS narrows
  // `dd.items_definition` to the right branch inside each block.
  const itemsDef = dd.items_definition;

  let items: BundleEditorItem[] = [];
  let groups: BundleEditorGroup[] = [];

  if (itemsDef.bundle_type === "fixed") {
    items = itemsDef.items.map((i) => {
      const { productTitle, variantTitle } = decomposeItemTitle(
        i.display_meta?.title
      );
      return {
        _tempId: crypto.randomUUID(),
        // Wire now carries `product_id` for new saves (needed by the
        // order endpoint's Shopify-line refine). Legacy items saved
        // before this field existed fall back to "" — the merchant
        // must re-pick those before the bundle can be checked out.
        productId: i.product_id ? stripShopifyGid(i.product_id) : "",
        variantId: stripShopifyGid(i.variant_id),
        productTitle,
        ...(variantTitle ? { variantTitle } : {}),
        ...(i.display_meta?.image_url
          ? { imageUrl: i.display_meta.image_url }
          : {}),
        basePrice: i.display_meta?.price
          ? parseFloat(i.display_meta.price) || 0
          : 0,
        quantity: i.quantity,
      };
    });
  } else {
    groups = itemsDef.groups.map((g) => ({
      _tempId: crypto.randomUUID(),
      groupId: g.group_id,
      name: g.name,
      pickCount: g.pick_count,
      options: g.options.map((opt) => {
        const { productTitle, variantTitle } = decomposeItemTitle(
          opt.display_meta?.title
        );
        return {
          _tempId: crypto.randomUUID(),
          productId: opt.product_id ? stripShopifyGid(opt.product_id) : "",
          variantId: stripShopifyGid(opt.variant_id),
          productTitle,
          ...(variantTitle ? { variantTitle } : {}),
          ...(opt.display_meta?.image_url
            ? { imageUrl: opt.display_meta.image_url }
            : {}),
          basePrice: 0,
        };
      }),
    }));
  }

  const { triggerProducts } = readTriggers(offer.triggers);

  return {
    internalName: offer.internal_name,
    isActive: offer.is_active,

    bundleId: target.bundle_id ?? crypto.randomUUID(),

    triggerProducts,

    bundleType: itemsDef.bundle_type,

    items,
    groups,

    basePrice: dd.base_price,
    compareAtPrice: dd.compare_at_price ?? 0,
    hideCompareAtPrice: dd.hide_compare_at_price ?? false,

    pricingAction,
    pricingAmount,
    pricingPercentage,

    titleOverride: dd.title_override ?? "",
    description: dd.description ?? "",
    imageUrl: dd.image_url ?? "",

    backgroundColor:
      (styles.background_color as string) ?? defaults.backgroundColor,
    borderColor: (styles.border_color as string) ?? defaults.borderColor,
    activeBorderColor:
      (styles.active_border_color as string) ?? defaults.activeBorderColor,
    borderRadius: (styles.border_radius as number) ?? defaults.borderRadius,
  };
}
